import { DocumentTypeLabelPipe } from '../../../pipes/document-type-label.pipe';
import { getDocumentTypeLabel } from '../../../helpers/document-type-label.helper';
import { Component, inject, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DriverDocuments, DocumentType, UploadProgressStatus, UpdateDriverDocument } from '../../../models/driver-documents.model';
import { DriverDocumentsService } from '../../../services/driver-documents.service';
import { FormsModule } from '@angular/forms';
import { ConfirmationOptions } from '../../../../../_shared/models/modals/confirmation-options.model';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-edit-driver-document-modal',
  standalone: true,
  imports: [DocumentTypeLabelPipe, FormsModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-driver-document-modal.component.html',
  styleUrl: './edit-driver-document-modal.component.scss'
})
export class EditDriverDocumentModalComponent implements OnInit {

  file: File | null = null;
  document: DriverDocuments;
  documentTypes: DocumentType[] = [];
  uploadedDocument: UploadProgressStatus | null = null;
  hasProfessionalDrivingPermit: boolean = false;
  isLoading: boolean = false;
  documentRenewalDate: string = '';
  licensePdpRenewalDate: string = '';
  placeholderDocumentDate: string = '';
  placeholderPdpDate: string = '';

  private modalInstance = inject(NgbActiveModal);
  private transloco = inject(TranslocoService);
  private driverDocumentsService = inject(DriverDocumentsService);
  private confirmationModalService = inject(ConfirmationModalService);
  private errorHandlerService = inject(ErrorHandlerService);

  private _hasLicensePdp = false;

  ngOnInit(): void {
    if (this.document.pdpValidUntil) {
      this.hasLicensePdp = true;
    }

    this.updatePlaceholders();
  }

  get documentTypeName(): string {
    return this.documentTypes.find(type => type.name === this.document.documentName)?.name || '';
  }

  get documentTypeExpires(): boolean {
    return this.documentTypes.find(type => type.name === this.document.documentName)?.validUntilRequired || false;
  }

  get hasLicensePdp(): boolean {
    return this._hasLicensePdp;
  }

  set hasLicensePdp(value: boolean) {
    this._hasLicensePdp = value;

    setTimeout(() => {
      this.updatePlaceholders();
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];

      const maxSizeInBytes = 5 * 1024 * 1024;
      if (selectedFile.size > maxSizeInBytes) {
        alert('File size must be less than or equal to 5MB.');
        input.value = '';
        return;
      }

      this.file = selectedFile;
      this.uploadTempDocument();
    }
  }

  async uploadTempDocument() {
    try {
      if (!this.file) return;

      this.uploadedDocument = {
        documentId: this.document.documentId,
        reference: crypto.randomUUID(),
        driverId: this.document.driverId,
        documentName: this.document.documentName,
        extension: this.file?.name ? `.${this.file.name.split('.').pop()}` : '',
        documentTypeId: this.documentTypes.find(type => type.name === this.document.documentName)?.id || 0,
        hasLicensePdp: this._hasLicensePdp,
        document: this.file,
        uploading: true,
        uploadProgress: 0,
        uploadSuccess: false
      };

      const tempDocFormData = this.getTempDocumentToUpload(this.uploadedDocument);
      this.driverDocumentsService.uploadTempDocuments(tempDocFormData).then(() => {
        this.uploadedDocument!.uploadProgress = 100;
        setTimeout(() => {
          this.uploadedDocument!.uploading = false;
          this.uploadedDocument!.uploadSuccess = true;

          setTimeout(() => {
            this.updatePlaceholders();
          });
          clearInterval(interval);
        }, 500);
      });

      const interval = setInterval(() => {
        if (this.uploadedDocument!.uploadProgress < 90) {
          this.uploadedDocument!.uploadProgress += 1;
        }
      }, 100);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  deleteFile() {
    this.file = null;
    this.uploadedDocument = null;
    this.documentRenewalDate = '';
    this.licensePdpRenewalDate = '';

    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
    setTimeout(() => {
      this.updatePlaceholders();
    });
  }

  async saveChanges() {
    this.isLoading = true;

    try {
      const uploadedDocument = this.getUploadedDocument();
      await this.driverDocumentsService.updateDriverDocument(uploadedDocument);

      this.modalInstance.close();
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  closeModal() {
    this.modalInstance.dismiss();
  }

  openDeleteDriverDocumentConfirmationModal(document: DriverDocuments) {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('driverDocuments.deletePrompt.title'),
      message: this.transloco.translate('driverDocuments.deletePrompt.message', { name: getDocumentTypeLabel(this.transloco, document.documentName) }),
      confirmText: this.transloco.translate('driverDocuments.deletePrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline')
    }

    this.confirmationModalService.showConfirmationModal(options).then(async () => {
      this.deleteFile();
    });
  }

  private updatePlaceholders(): void {
    this.placeholderDocumentDate = this.formatPlaceHolderDate(this.document?.validUntil);
    this.placeholderPdpDate = this.formatPlaceHolderDate(this.document?.pdpValidUntil);
  }

  private formatPlaceHolderDate(date?: Date | string | null): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  }

  private getTempDocumentToUpload(doc: UploadProgressStatus): FormData {
    const formData = new FormData();
    formData.append('DriverId', doc.driverId.toString());
    formData.append('DocumentName', doc.documentName);
    formData.append('Reference', doc.reference);
    formData.append('Document', doc.document);

    return formData;
  }

  private getUploadedDocument(): UpdateDriverDocument {
    return {
      documentId: this.uploadedDocument!.documentId,
      driverId: this.uploadedDocument!.driverId,
      documentName: this.uploadedDocument!.documentName,
      extension: this.uploadedDocument!.extension,
      documentTypeId: this.uploadedDocument!.documentTypeId,
      reference: this.uploadedDocument!.reference,
      validUntil: this.documentRenewalDate ? this.documentRenewalDate : null,
      pdpValidUntil: this.licensePdpRenewalDate ? this.licensePdpRenewalDate : null,
      hasLicensePdp: this.licensePdpRenewalDate ? true : false
    };
  }
}